<script setup lang="ts">
import { fetchTicketCategoriesData, queryKey, type InfoData } from '~/queries'

type Props = {
  product: Exclude<InfoData['product'], undefined>
  class?: string
}

const props = defineProps<Props>()

const classes = computed(() => ({
  'app-tickets-event': true,
  [`${props.class}`]: !!props.class,
}))

const modals = useModalsStore()
const site = useSiteStore()

const { data: ticketCategoriesData, status: fetchStatus } = await useAsyncData(
  queryKey('ticket-categories', props.product?.id),
  async () => {
    if (!props.product?.id) return

    return await fetchTicketCategoriesData({
      productId: props.product.id,
    })
  },
  { server: false, lazy: true },
)

const isFetched = computed(() => !(fetchStatus.value === 'pending' || fetchStatus.value === 'idle'))
const categories = computed(() => ticketCategoriesData.value?.items)

const hasTickets = computed(() => {
  const now = new Date()

  if (!categories.value?.length) {
    return false
  }

  if (props.product.startDate) {
    return new Date(props.product.startDate) >= now
  }

  return true
})
</script>

<template>
  <div :class="classes">
    <template v-if="hasTickets">
      <AppTicket
        v-for="category in categories"
        :key="category.id"
        :title="category.title"
        :description="category.description"
        :price="formatCurrency(category.price)"
        :was-price="category.wasPrice ? formatCurrency(category.wasPrice) : null"
        :saved="category.wasPrice ? category.wasPrice - category.price : null"
        class="mb-1 md:mb-3"
      >
        <AppButton
          color="black"
          size="small"
          full-width
          @click="
            modals.openAddToCartModal({
              title: category.title,
              productId: product.id,
              ticketCategoryId: category.id,
              price: category.price,
              minQuantity: category.minQuantity,
              maxQuantity: category.maxQuantity,
            })
          "
        >
          {{ $t('string.Add') }}
        </AppButton>
      </AppTicket>
    </template>

    <AppPaper v-else-if="isFetched" class="relative p-3 pt-6 md:p-16">
      <div v-if="product?.soldOut?.message" class="pb-4 reset-list-styles" v-html="product.soldOut.message" />
      <p
        v-else
        v-html="
          $t('page.product.unavailable.description', {
            email: site.data?.emailAddress,
          })
        "
      />
      <AppButton v-if="product?.soldOut?.modal" full-width @click="modals.openContactUsModal()">
        {{ $t('page.faqs.extra.contact.title') }}
      </AppButton>
    </AppPaper>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';
</style>
